import React from "react";

import {
  Platform,
  View,
  Dimensions,
  ScrollView,
  Alert,
  KeyboardAvoidingView,
} from "react-native";

import AppConfig from "../../../config/AppConfig";

import Utils, {
  getActivityModel,
  getTagModel,
  hasItems,
  hasExactMatch,
  getStateData,
  getActivitiesWithTags,
  isWeb,
  escapeRegexp,
  sortByRelevance,
  navigate,
  isAndroid,
  getSetModel,
} from "../../../helpers/utils";

import Header from "../common/header";
import Footer from "../common/footer";

import ActivitiesFabButton from "./ActivitiesFabButton";

import Search from "../common/search-bar/Search";
import MenuEditOptionsPanel from "../common/MenuEditOptionsPanel";
import ItemNotFound from "../common/ItemNotFound";
import ActivitiesSets from "./groups/ActivitiesSets";
import ActivitiesList from "./ActivitiesList/ActivitiesList";
import TagsList from "../tags/TagsList/TagsList";
import { AppStateWatcher } from "../common/AppStateWatcher";
import LoadingIndicator from "../common/LoadingIndicator";
import appStyles from "../../appStyles";

const { height: HEIGHT } = Dimensions.get("window");

export default class ActivitiesPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;
    this.userAvatar = props.controller.state
      ? props.controller.state.picture
      : null;

    this.state = {
      isLoading: true,
      activities: [],
      showEditPanel: false,
      showEditTagPanel: false,
      sets: [],
    };
  }

  componentDidMount = async () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.componentDidFocus();
        },
      );
    } else {
      this.filterResults({});
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  componentDidFocus = async () => {
    this.filterResults({});
  };

  showEditOptions = (activity) => {
    this.setState((state) => ({
      ...state,
      showEditPanel: true,
      selectedActivity: activity,
    }));
  };

  closeEditOptions = () => {
    this.setState((state) => ({
      ...state,
      selectedActivity: null,
      selectedTag: null,
      showEditPanel: false,
      showEditTagPanel: false,
    }));
  };

  showEditTagOptions = (tag) => {
    this.setState((state) => ({
      ...state,
      showEditTagPanel: true,
      selectedTag: tag,
    }));
  };

  filterByKeyword = ({ term }) => {
    this.filterResults({ ...this.state.filters, term });
  };

  advancedFiltering = (filters) => {
    this.filterResults({ ...this.state.filters, ...filters });
  };

  getActivitiesList = () => {
    let allActivities = getActivitiesWithTags(
      this.controller.state,
      [],
      "name",
    );

    allActivities.sort((a, b) => {
      if (a.is_favorite == b.is_favorite) {
        return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
      }
      return (a.is_favorite || 0) > (b.is_favorite || 0) ? -1 : 1;
    });

    return allActivities;
  };

  filterResults = (filters) => {
    const {
      term = null,
      showActivities = true,
      showTags = true,
      showFavorites = true,
      showArchived = false,
      sets = [],
    } = filters != null ? filters : {};
    let filteredActivities = showActivities ? this.getActivitiesList() : [];
    let filteredTags = showTags
      ? getStateData(this.controller.state, "tags", [], "name")
      : [];

    if (!showArchived) {
      filteredActivities = filteredActivities.filter((a) => a.is_active);
    }

    if (!showFavorites) {
      filteredActivities = filteredActivities.filter((a) => a.is_favorite != 1);
    }

    let filteredSets = getStateData(this.controller.state, "sets", [], "name");

    // Multi terms search
    if (term) {
      const pattern = Utils.getSearchPattern(term);
      // SETS
      filteredSets = filteredSets.filter((a) => {
        return a.name && a.name.match(pattern);
      });

      // TAGS
      filteredTags = filteredTags.filter((a) => {
        return a.name && a.name.match(pattern);
      });

      const tagIds = filteredTags.map((t) => t.id);

      // ACTVITIES: filter by name or by tags
      filteredActivities = filteredActivities.filter((a) => {
        return (
          (a.name && a.name.match(pattern)) ||
          (a.tags &&
            a.tags.filter((t) => {
              return tagIds.includes(t);
            }).length)
        );
      });
    }

    // Filter by sets
    if (hasItems(sets)) {
      filteredActivities = filteredActivities.filter((a) => {
        if (!a.sets) return false;

        // Check if activity sets contains any selected set id
        const selectedSets = a.sets.filter((setId) => sets.includes(setId));
        return hasItems(selectedSets);
      });
    }

    this.setState((state) => ({
      ...state,
      isLoading: false,
      activities: sortByRelevance(filteredActivities, term),
      tags: filteredTags,
      filters,
      sets: filteredSets,
    }));
  };

  addActivity = (name = "") => {
    this.controller.changePage("feed", {
      selectedPage: "rn:edit_activity",
      args: {
        activity: getActivityModel({ name }),
      },
    });
  };

  editActivity = (selectedActivity) => {
    this.controller.changePage("feed", {
      selectedPage: "rn:edit_activity",
      args: {
        activity: getActivityModel(selectedActivity),
      },
    });
  };

  editMenuActivity = () => {
    const { selectedActivity } = this.state;
    if (selectedActivity) this.editActivity(selectedActivity);
  };

  archiveMenuActivity = () => {
    if (isWeb) {
      this.confirmedArchive();
      this.closeEditOptions();
      return;
    }

    Alert.alert(
      "Archive activity",
      "Are you sure you want to remove this activity?",
      [
        {
          text: "Cancel",
          style: "cancel",
        },
        {
          text: "OK",
          onPress: this.confirmedArchive,
        },
      ],
      { cancelable: true },
    );
  };

  confirmedArchive = () => {
    const { selectedActivity } = this.state;
    if (!selectedActivity) return;

    this.controller.tracking.track("Edit Element");
    console.log("Edit Element");
    this.controller.cardActionWithCallback(
      {
        action: "save",
        target: "activity",
        edit_id: selectedActivity.id,
        edit_name: selectedActivity.name,
        edit_is_active: false,
      },
      () => {
        //this.controller.cardAction({ action: "refresh" });
        this.filterResults({ ...this.state.filters });
        this.closeEditOptions();
      },
    );
  };

  copyMenuActivity = () => {
    const { selectedActivity } = this.state;
    if (!selectedActivity) return;

    let model = getActivityModel();
    model.edit_icon = selectedActivity.icon;

    this.controller.changePage("feed", {
      selectedPage: "rn:edit_activity",
      args: {
        activity: model,
      },
    });
  };

  onSelectTag = (t) => {
    this.controller.changePage("feed", {
      selectedPage: "rn:tag_activities",
      args: {
        tag: getTagModel(t),
      },
    });
  };

  addTag = (name = "") => {
    this.controller.changePage("feed", {
      selectedPage: "rn:edit_tag",
      args: {
        tag: getTagModel({ name }),
        returnUrl: "rn:activities",
      },
    });
  };

  editMenuTag = () => {
    const { selectedTag } = this.state;
    if (!selectedTag) return;

    this.controller.changePage("feed", {
      selectedPage: "rn:edit_tag",
      args: {
        tag: getTagModel(selectedTag),
        returnUrl: "rn:activities",
      },
    });
  };

  copyMenuTag = () => {
    const { selectedTag } = this.state;
    if (!selectedTag) return;

    this.controller.changePage("feed", {
      selectedPage: "rn:edit_tag",
      args: {
        tag: {
          edit_id: "",
          edit_name: selectedTag.name,
          edit_icon: selectedTag.icon,
          edit_is_active: "checked",
        },
      },
    });
  };

  archiveMenuTag = () => {
    const { selectedTag } = this.state;
    if (!selectedTag) return;

    this.controller.tracking.track("Archive Tag");
    this.controller.cardAction({
      action: "save",
      target: "tag",
      edit_id: selectedTag.id,
      edit_name: selectedTag.name,
      edit_is_active: false,
      requestPage: "rn:activities",
      requestArgs: {},
    });
  };

  editSet = ({ selectedSet }) => {
    this.setState((state) => ({ ...state, isLoading: true }));

    navigate(this.controller, "rn:edit_set", "changepage", {
      set: getSetModel(selectedSet),
    });
  };

  render() {
    const { isLoading, activities, tags, sets, filters } = this.state;

    const flt =
      filters != null && filters.term != null ? filters.term.trim() : "";

    return (
      <>
        <View style={appStyles.container}>
          <KeyboardAvoidingView
            style={{
              height: isAndroid ? HEIGHT : isWeb ? HEIGHT - 50 : HEIGHT - 75,
            }}
            behavior="height"
          >
            <Header
              title="Elements"
              subTitle="Organize your activities with tags & groups"
              avatar={this.userAvatar}
              controller={this.controller}
              showMore
              hideAvatar
            />

            {isLoading ? (
              <LoadingIndicator isLoading={isLoading} />
            ) : (
              <ScrollView
                style={appStyles.scrollView}
                contentContainerStyle={appStyles.scrollViewContent}
              >
                {hasItems(activities) ? (
                  <ActivitiesList
                    title="All activities"
                    activities={activities}
                    contentType="activities"
                    showEditOptions={this.showEditOptions}
                    initialNumToRender={AppConfig.NUM_OF_ACTIVITIES}
                    controller={this.controller}
                    onActivitySelected={this.editActivity}
                  />
                ) : null}

                {flt.length > 0 && !hasExactMatch(activities, flt) ? (
                  <ItemNotFound
                    name={flt}
                    type="activities"
                    create={() => this.addActivity(flt)}
                  />
                ) : null}

                {hasItems(tags) ? (
                  <TagsList
                    title="Tags"
                    tags={tags}
                    isReadOnly
                    initialNumToRender={10}
                    onSelectTag={this.onSelectTag}
                    showEditOptions={this.showEditTagOptions}
                  />
                ) : null}

                {flt.length > 0 && !hasExactMatch(tags, flt) ? (
                  <ItemNotFound
                    name={flt}
                    type="tags"
                    create={() => this.addTag(flt)}
                  />
                ) : null}

                <ActivitiesSets
                  title="Core Elements"
                  sets={sets}
                  callback={this.editSet}
                  preSelected={true}
                  hideCollapse
                  initialNumToRender={AppConfig.NUM_OF_SETS}
                />
              </ScrollView>
            )}

            <MenuEditOptionsPanel
              isPanelActive={this.state.showEditPanel}
              close={this.closeEditOptions}
              options={[
                {
                  text: "Edit Element",
                  action: this.editMenuActivity,
                },
                {
                  text: "Copy Element",
                  action: this.copyMenuActivity,
                },
                {
                  text: "Archive Element",
                  action: this.archiveMenuActivity,
                  isDelete: true,
                },
              ]}
            />

            <MenuEditOptionsPanel
              isPanelActive={this.state.showEditTagPanel}
              close={this.closeEditOptions}
              options={[
                {
                  text: "Edit Tag",
                  action: (item) => this.editMenuTag(item),
                },
                {
                  text: "Copy Tag",
                  action: () => this.copyMenuTag(item),
                },
              ]}
            />

            <View style={[appStyles.footerOptions]}>
              <ActivitiesFabButton
                addActivity={this.addActivity}
                addTag={this.addTag}
                style={{ bottom: Platform.OS === "android" ? 126 : 63 }}
              />

              <Search
                style={Platform.OS === "android" ? { bottom: 63 } : {}}
                filterResults={this.filterByKeyword}
                advancedFiltering={this.advancedFiltering}
              />
            </View>
          </KeyboardAvoidingView>

          {this.controller.hasNavigation() ? null : (
            <View style={appStyles.footerOptions}>
              <Footer controller={this.controller} selectedPage="activities" />
            </View>
          )}

          <AppStateWatcher
            onAppBecomesActive={() => this.filterResults(filters)}
          />
        </View>
      </>
    );
  }
}
